import { useEffect, useState, useCallback, useRef } from "react";
import { TURNSTILE_TOKEN_TIMEOUT_MS } from "@/config";
import { TurnstileContext } from "./TurnstileContext";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";

export type TurnstileProviderProps = {
  children: React.ReactNode;
};

type TokenWaiter = (token: string) => void;

export const TurnstileProvider = ({ children }: TurnstileProviderProps) => {
  const [token, setTokenState] = useState<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  // Refs, not state: `getFreshToken` is called from closures created at click
  // time, after a 3DS challenge that can outlive the token. Only refs let it
  // see the token the widget issued since then.
  const tokenRef = useRef<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenWaitersRef = useRef<TokenWaiter[]>([]);

  useEffect(() => {
    if (window.turnstile) {
      setIsScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src^="https://challenges.cloudflare.com/turnstile"]`,
    );

    if (existingScript) {
      window.onTurnstileLoad = () => {
        setIsScriptLoaded(true);
      };
      return;
    }

    window.onTurnstileLoad = () => {
      setIsScriptLoaded(true);
    };

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.defer = true;

    document.head.appendChild(script);

    return () => {
      delete window.onTurnstileLoad;
    };
  }, []);

  const setToken = useCallback((nextToken: string | null) => {
    tokenRef.current = nextToken;
    setTokenState(nextToken);

    if (nextToken === null) return;

    const waiters = tokenWaitersRef.current.splice(0);
    waiters.forEach((resolve) => resolve(nextToken));
  }, []);

  const setWidgetId = useCallback((id: string | null) => {
    widgetIdRef.current = id;
  }, []);

  const resetTurnstile = useCallback(() => {
    setToken(null);
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [setToken]);

  const hasValidToken = () => {
    if (tokenRef.current === null) return false;

    const widgetId = widgetIdRef.current;
    if (!window.turnstile || !widgetId) return true;

    return !window.turnstile.isExpired(widgetId);
  };

  const waitForNextToken = () =>
    new Promise<string | null>((resolve) => {
      const waiter: TokenWaiter = (nextToken) => {
        clearTimeout(deadline);
        resolve(nextToken);
      };
      const deadline = setTimeout(() => {
        tokenWaitersRef.current = tokenWaitersRef.current.filter(
          (pending) => pending !== waiter,
        );
        resolve(null);
      }, TURNSTILE_TOKEN_TIMEOUT_MS);

      tokenWaitersRef.current.push(waiter);
    });

  const getFreshToken = useCallback(async () => {
    if (hasValidToken()) return tokenRef.current;

    resetTurnstile();
    return waitForNextToken();
  }, [resetTurnstile]);

  const value = {
    token,
    isReady: token !== null,
    isScriptLoaded,
    resetTurnstile,
    setToken,
    setWidgetId,
    getFreshToken,
  };

  return <TurnstileContext value={value}>{children}</TurnstileContext>;
};
